import React, {useMemo} from 'react';

import {type OnRetryFetchMore} from '../LogViewerFetchMoreError';
import {
  LogViewerPageLoader,
  type RenderFetchMoreError,
} from '../LogViewerPageLoader';

export interface LogViewerVirtuosoContext {
  // Backward pagination
  isLoadingPrevious: boolean;
  isRetryingPrevious: boolean;
  loadPreviousError?: Error | string;
  onRetryPrevious?: OnRetryFetchMore;
  renderFetchPreviousError?: RenderFetchMoreError;
  // Forward pagination
  isLoadingNext: boolean;
  isRetryingNext: boolean;
  loadNextError?: Error | string;
  onRetryNext?: OnRetryFetchMore;
  renderFetchNextError?: RenderFetchMoreError;
}

/**
 * useVirtuosoComponents creates cached component classes used for the
 * `LogViewer`'s header and footer elements. These are displayed when loading
 * additional logs or when errors occur during pagination.
 */
export function useVirtuosoComponents({
  isPolling,
  // Backward pagination (Header)
  isLoadingPrevious,
  isRetryingPrevious,
  loadPreviousError,
  onRetryPrevious,
  renderFetchPreviousError,
  // Forward pagination (Footer)
  isLoadingNext,
  isRetryingNext,
  loadNextError,
  onRetryNext,
  renderFetchNextError,
}: {
  isPolling: boolean;
  // Backward pagination
  isLoadingPrevious: boolean;
  isRetryingPrevious: boolean;
  loadPreviousError?: Error | string;
  onRetryPrevious?: OnRetryFetchMore;
  renderFetchPreviousError?: RenderFetchMoreError;
  // Forward pagination
  isLoadingNext: boolean;
  isRetryingNext: boolean;
  loadNextError?: Error | string;
  onRetryNext?: OnRetryFetchMore;
  renderFetchNextError?: RenderFetchMoreError;
}) {
  const components = useMemo(
    () => ({
      Header: ({context}: {context?: LogViewerVirtuosoContext}) => (
        <LogViewerPageLoader
          error={context?.loadPreviousError}
          isLoading={context?.isLoadingPrevious ?? false}
          isRetrying={context?.isRetryingPrevious ?? false}
          onRetry={context?.onRetryPrevious}
          renderError={context?.renderFetchPreviousError}
        />
      ),
      Footer: ({context}: {context?: LogViewerVirtuosoContext}) => (
        <LogViewerPageLoader
          error={context?.loadNextError}
          isLoading={context?.isLoadingNext ?? false}
          isRetrying={context?.isRetryingNext ?? false}
          onRetry={isPolling ? undefined : context?.onRetryNext}
          renderError={context?.renderFetchNextError}
        />
      ),
    }),
    [isPolling]
  );

  const context: LogViewerVirtuosoContext = {
    // Backward pagination
    isLoadingPrevious,
    isRetryingPrevious,
    loadPreviousError,
    onRetryPrevious,
    renderFetchPreviousError,
    // Forward pagination
    isLoadingNext,
    isRetryingNext,
    loadNextError,
    onRetryNext,
    renderFetchNextError,
  };

  return {
    components,
    context,
  };
}
